import { reactive } from 'vue'

export const SUPPORTED_LOCALES = Object.freeze(['it', 'en'])

export const LOCALE_OPTIONS = Object.freeze([
  { code: 'it', flag: '🇮🇹', shortLabel: 'IT', label: 'Italiano' },
  { code: 'en', flag: '🇬🇧', shortLabel: 'EN', label: 'English' }
])

const STORAGE_KEY = 'bullverge-locale'

const messages = Object.freeze({
  it: {
    common: {
      backToTop: 'Torna su',
      close: 'Chiudi',
      copied: 'Link copiato',
      manualCopy: 'Copia manuale',
      shared: 'Condiviso'
    },
    language: {
      label: 'Lingua',
      select: 'Seleziona lingua',
      italian: 'Italiano',
      english: 'Inglese'
    },
    nav: {
      mainLabel: 'Navigazione principale',
      mobileLabel: 'Menu mobile',
      openMenu: 'Apri il menu',
      social: 'Social',
      supportProject: 'Sostieni il progetto',
      supportBullVerge: 'Sostieni BullVerge',
      links: {
        home: 'Home',
        ecosystem: 'Ecosistema',
        products: 'Prodotti',
        prometeo: 'Prometeo',
        nemo: 'Nemo',
        backsense: 'BackSense',
        aura: 'Aura',
        modules: 'Moduli',
        aegis: 'Aegis',
        tutorials: 'Casi d’uso',
        academy: 'Academy',
        papers: 'Paper',
        researchNotes: 'Note ricerca / R&D',
        faq: 'FAQ',
        contacts: 'Contatti'
      },
      descriptions: {
        ecosystem: 'Prodotti, moduli e impatto',
        products: 'Linee prodotto BullVerge',
        prometeo: 'Dispositivo edge AI locale',
        nemo: 'Assistente per routine e cura',
        backsense: 'Consapevolezza aptica 180',
        aura: 'Versione per sicurezza personale',
        modules: 'Capacità BullVerge e modi d’uso',
        tutorials: 'Casi d’uso e adozione',
        academy: 'Paper, note R&D e FAQ',
        papers: 'Documenti tecnici e white paper',
        researchNotes: 'Direzioni pubbliche di ricerca',
        faq: 'Risposte rapide',
        contacts: 'Team e collaborazioni'
      }
    },
    footer: {
      rights: 'Tutti i diritti riservati.'
    },
    modelViewer: {
      title: 'Caricamento modello 3D',
      initializing: 'Inizializzazione visualizzatore...',
      contextLost: 'Motore grafico interrotto, ripristino...',
      cacheLoad: 'Recupero modello dalla cache applicativa...',
      retrying: 'Nuovo tentativo automatico ({count})...',
      invalidScene: 'GLB senza scena valida',
      finalizing: 'Finalizzazione rendering...',
      downloading: 'Scaricamento risorsa 3D...',
      receiving: 'Ricezione dati modello...',
      cacheRecovery: 'Ripristino cache modello...',
      slowRetry: 'Connessione lenta, nuovo tentativo tra {seconds}s...',
      ready: 'Modello pronto',
      unstableRender: 'Render non stabilizzato, ripristino...',
      restoring: 'Ripristino grafica...',
      restoring3d: 'Ripristino render 3D...'
    },
    prometeoViewer: {
      toolbarLabel: 'Controlli modello 3D Prometeo',
      cover: 'Coperchio',
      coverModes: {
        open: 'Aperto',
        closed: 'Chiuso',
        auto: 'Auto'
      },
      interaction: {
        touch: '1 dito: ruota · 2 dita: zoom o sposta il dispositivo',
        pointer: 'Trascina per ruotare · Rotella per zoom · Tasto destro + trascina per spostare il dispositivo'
      },
      fallback: 'Modello 3D non disponibile al momento.',
      loadingMessages: [
        'BullVerge sta caricando...',
        'Ottimizzando le texture...',
        'Sincronizzando le animazioni...',
        'Preparando la scena 3D...'
      ]
    },
    paper: {
      new: 'Nuovo',
      read: 'Leggi il paper',
      soon: 'Presto disponibile...',
      note: 'Aggiorniamo continuamente i contenuti per condividere con partner e interlocutori tecnici l’evoluzione del nostro stack edge AI. Richiedi accesso anticipato per co-progettare casi d’uso verticali.',
      readerLabel: 'Modalità lettura paper',
      share: 'Condividi paper',
      category: 'Categoria',
      keyPoints: 'Punti chiave',
      techHint: 'Passa alla modalità <strong>Tecnica</strong> per la versione completa con dettagli metodologici.',
      techFallback: 'Contenuto tecnico disponibile in modalità tecnica.',
      hero: {
        badge: 'Base conoscenza',
        title: 'Documenti tecnici e white paper',
        copy: 'Esplora il cuore tecnologico di BullVerge: ricerche proprietarie, pipeline edge AI locali e strategie che raccontano come costruiamo AI autonoma, scalabile e sostenibile.'
      }
    }
  },
  en: {
    common: {
      backToTop: 'Back to top',
      close: 'Close',
      copied: 'Link copied',
      manualCopy: 'Manual copy',
      shared: 'Shared'
    },
    language: {
      label: 'Language',
      select: 'Select language',
      italian: 'Italian',
      english: 'English'
    },
    nav: {
      mainLabel: 'Main navigation',
      mobileLabel: 'Mobile menu',
      openMenu: 'Open menu',
      social: 'Social',
      supportProject: 'Support the project',
      supportBullVerge: 'Support BullVerge',
      links: {
        home: 'Home',
        ecosystem: 'Ecosystem',
        products: 'Products',
        prometeo: 'Prometeo',
        nemo: 'Nemo',
        backsense: 'BackSense',
        aura: 'Aura',
        modules: 'Modules',
        aegis: 'Aegis',
        tutorials: 'Use cases',
        academy: 'Academy',
        papers: 'Papers',
        researchNotes: 'Research Notes / R&D',
        faq: 'FAQ',
        contacts: 'Contact'
      },
      descriptions: {
        ecosystem: 'Products, modules and impact',
        products: 'BullVerge product lines',
        prometeo: 'Local edge AI device',
        nemo: 'Routine and care assistant',
        backsense: '180 haptic awareness',
        aura: 'Personal safety edition',
        modules: 'BullVerge capabilities and use cases',
        tutorials: 'Application and adoption paths',
        academy: 'Papers, R&D notes and FAQ',
        papers: 'White papers and technical documents',
        researchNotes: 'Public research directions',
        faq: 'Fast answers',
        contacts: 'Team and collaborations'
      }
    },
    footer: {
      rights: 'All rights reserved.'
    },
    modelViewer: {
      title: 'Loading 3D model',
      initializing: 'Initializing viewer...',
      contextLost: 'Graphics engine interrupted, restoring...',
      cacheLoad: 'Loading model from cache/app...',
      retrying: 'Automatic retry ({count})...',
      invalidScene: 'GLB has no valid scene',
      finalizing: 'Finalizing render...',
      downloading: 'Downloading 3D asset...',
      receiving: 'Receiving model data...',
      cacheRecovery: 'Restoring model cache...',
      slowRetry: 'Slow connection, retrying in {seconds}s...',
      ready: 'Model ready',
      unstableRender: 'Render did not stabilize, restoring...',
      restoring: 'Restoring graphics...',
      restoring3d: 'Restoring 3D render...'
    },
    prometeoViewer: {
      toolbarLabel: 'Prometeo 3D model controls',
      cover: 'Cover',
      coverModes: {
        open: 'Open',
        closed: 'Closed',
        auto: 'Auto'
      },
      interaction: {
        touch: '1 finger: rotate · 2 fingers: zoom or move the device',
        pointer: 'Drag to rotate · Wheel to zoom · Right-click + drag to move the device'
      },
      fallback: '3D model is not available right now.',
      loadingMessages: [
        'BullVerge is loading...',
        'Optimizing textures...',
        'Syncing motion actions...',
        'Preparing the 3D scene...'
      ]
    },
    paper: {
      new: 'New',
      read: 'Read the paper',
      soon: 'Available soon...',
      note: 'We continuously update these contents to share the evolution of our edge AI stack with partners and stakeholders. Request early access to co-design vertical use cases.',
      readerLabel: 'Paper reading mode',
      share: 'Share paper',
      category: 'Category',
      keyPoints: 'Key points',
      techHint: 'Switch to <strong>Tech</strong> mode for the complete version with methodological details.',
      techFallback: 'Technical content is available in Tech mode.',
      hero: {
        badge: 'Knowledge Base',
        title: 'Technical Documents and White Papers',
        copy: 'Explore BullVerge technology: proprietary research, edge-native pipelines and strategies that explain how we build autonomous, scalable and sustainable AI.'
      }
    }
  }
})

export const i18nState = reactive({
  locale: detectInitialLocale()
})

function isSupportedLocale(locale) {
  return SUPPORTED_LOCALES.includes(String(locale || '').toLowerCase())
}

function normalizeLocale(locale) {
  const normalized = String(locale || '').toLowerCase().slice(0, 2)
  return isSupportedLocale(normalized) ? normalized : 'en'
}

function safeLocalStorageGet(key) {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : ''
  } catch (_) {
    return ''
  }
}

function safeLocalStorageSet(key, value) {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, value)
  } catch (_) {
    // Storage can be unavailable in privacy-restricted contexts.
  }
}

function localeFromQuery() {
  if (typeof window === 'undefined') return ''
  try {
    return new URLSearchParams(window.location.search).get('lang') || ''
  } catch (_) {
    return ''
  }
}

function browserLooksItalian() {
  if (typeof navigator === 'undefined') return false
  const languages = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language]

  return languages.some((language) => {
    const normalized = String(language || '').toLowerCase()
    return normalized === 'it' || normalized.startsWith('it-') || normalized.endsWith('-it')
  })
}

function locationLooksItalian() {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (timeZone === 'Europe/Rome') return true
  } catch (_) {
    return false
  }
  return false
}

function detectInitialLocale() {
  const queryLocale = normalizeLocale(localeFromQuery())
  if (isSupportedLocale(queryLocale) && localeFromQuery()) return queryLocale

  const savedLocale = normalizeLocale(safeLocalStorageGet(STORAGE_KEY))
  if (isSupportedLocale(savedLocale) && safeLocalStorageGet(STORAGE_KEY)) return savedLocale

  if (locationLooksItalian() || browserLooksItalian()) return 'it'
  return 'en'
}

function readMessage(locale, path) {
  return String(path || '').split('.').reduce((value, key) => {
    if (value && Object.prototype.hasOwnProperty.call(value, key)) return value[key]
    return undefined
  }, messages[locale])
}

function formatMessage(value, params = {}) {
  if (typeof value !== 'string') return value
  return value.replace(/\{(\w+)\}/g, (match, key) => {
    return Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match
  })
}

export function t(path, params = {}) {
  const locale = i18nState.locale
  const value = readMessage(locale, path)
  const fallback = readMessage('it', path)
  return formatMessage(value ?? fallback ?? path, params)
}

export function tm(path) {
  const locale = i18nState.locale
  const value = readMessage(locale, path)
  const fallback = readMessage('it', path)
  return value ?? fallback
}

export function setLocale(locale, options = {}) {
  const nextLocale = normalizeLocale(locale)
  i18nState.locale = nextLocale
  if (options.persist !== false) safeLocalStorageSet(STORAGE_KEY, nextLocale)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = nextLocale
  }
}

export function currentLocale() {
  return i18nState.locale
}

export function i18nPlugin(app) {
  setLocale(i18nState.locale, { persist: false })
  app.config.globalProperties.$i18n = i18nState
  app.config.globalProperties.$t = t
  app.config.globalProperties.$tm = tm
  app.config.globalProperties.$setLocale = setLocale
  app.provide('i18n', {
    state: i18nState,
    t,
    tm,
    setLocale
  })
}

export default i18nPlugin
